import type { ReactNode } from 'react'

import { useContext, createContext, useState } from 'react'
import { MdInfoOutline } from 'react-icons/md'

type Keys = 'delivery-history' | 'obstetrical-history'

interface Context {
  warnings: Record<Keys, string | null>
  show: (key: string, warning: string) => void
  hide: (key: string) => void
}

const DeliveredContext = createContext<Context>({
  warnings: {
    'delivery-history': null,
    'obstetrical-history': null
  },
  show: () => ({}),
  hide: () => ({})
})

interface Props {
  children: ReactNode
}

export function DeliveredContextProvider ({ children }: Props): JSX.Element {
  const [ warnings, setWarnings ] = useState<Record<string, string | null>>({})

  function show (key: string, warning: string): void {
    setWarnings((prev) => ({ ...prev, [key]: warning }))
  }

  function hide (key: string): void {
    setWarnings((prev) => ({ ...prev, [key]: null }))
  }

  return (
    <DeliveredContext.Provider value={{ show, hide, warnings }}>
      {children}
    </DeliveredContext.Provider>
  )
}

interface UseLocalContext {
  warning: string | null
  show: (warning: string) => void
  hide: () => void

}

interface UseGlobalContext {
  warnings: Record<Keys, string | null>
  show: (key: Keys, warning: string) => void
  hide: (key: Keys) => void
}

interface ContextWarningProps {
  contextKey: Keys
  className: string
}

export function ContextWarning ({ contextKey, className }: ContextWarningProps): JSX.Element | null {
  const { warning } = useWarning(contextKey)

  if (!warning) {
    return null
  }

  return (
    <div className={className}>
      <div className='flex flex-row gap-4 border-1 border-solid rounded-xl border-primary px-4 py-3 items-center'>
        <MdInfoOutline
          size={25}
          color='rgb(1, 101, 255)'
        />
        <span>{warning}</span>
      </div>
    </div>
  )
}

export function useWarning (key: Keys): UseLocalContext {
  const context = useContext(DeliveredContext)

  return {
    hide: () => context.hide(key),
    show: (warning: string) => context.show(key, warning),
    warning: context.warnings[key] ?? null
  }
}

export function useWarnings (): UseGlobalContext {
  const context = useContext(DeliveredContext)

  return {
    ...context
  }
}
